<template>
  <div class="col-md-10 col-sm-9 u-pad-all-25 c-common-box-shadow u-bg-color-fff configChatVue">
    <h3 class="u-cmn-botmargin">Chat</h3>
    <p class="o-blue-regular-text">
      Chat for {{ clientName }}: whether it is on, when people can post, and which
      channels exist. Membership comes from CS3, so the groups fill themselves.
    </p>

    <!-- #2099 — the preview banner is a PROMISE that nothing is saved, so it
         must disappear exactly when that stops being true, and not before. -->
    <div v-if="!isLive" class="cc__preview" data-testid="cc-preview-banner">
      <i class="fa fa-flask" aria-hidden="true"></i>
      <span>
        <strong>Preview.</strong> The chat service is not built yet, so everything here is
        example data and <strong>nothing is saved</strong>. Switches move so the screen can be
        reviewed; reload and they return to where they were.
      </span>
    </div>

    <!-- #2099 — in a live build, what the screen is doing with the server. -->
    <div v-else-if="liveState !== 'ready'" class="cc__preview" data-testid="cc-live-state">
      <i class="fa" :class="liveState === 'failed' ? 'fa-exclamation-triangle' : 'fa-spinner fa-spin'"
         aria-hidden="true"></i>
      <span>{{ liveState === 'failed' ? liveErrorText : 'Loading this client’s chat settings…' }}</span>
    </div>
    <div v-else-if="saveError" class="cc__preview" data-testid="cc-save-error">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <span>
        {{ liveErrorText }} Your change has not been saved.
        <!-- #2099 asked for the service's refusal shown plainly. Its own words,
             not a paraphrase: it is the thing that knows which field and why. -->
        <template v-if="saveFieldErrors.length">
          <br>
          <span v-for="(f, i) in saveFieldErrors" :key="f.field + i" class="cc__fielderr">
            <code>{{ f.field }}</code> {{ f.message }}
          </span>
        </template>
      </span>
    </div>
    <div v-else-if="saving" class="cc__preview" data-testid="cc-saving">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      <span>Saving…</span>
    </div>
    <div v-else-if="savedAt" class="cc__preview cc__saved" data-testid="cc-saved">
      <i class="fa fa-check" aria-hidden="true"></i>
      <span>Saved. These settings are live for everyone in {{ clientName }}.</span>
    </div>

    <!-- ⚠ #2099 — NOTHING IS RENDERED UNTIL THE REAL SETTINGS ARE IN.
         Drawing the switches first would show every one of them OFF while the
         request was in flight, and a Client Admin reading "chat is off" when it
         is on — or worse, flicking a switch against a state the screen has not
         loaded yet and saving it — is the opposite of what this screen is for. -->
    <template v-if="!isLive || liveState === 'ready'">

    <!-- ── the on/off switch ─────────────────────────────────────────────── -->
    <div class="cc__master" data-testid="cc-master">
      <div class="cc__rowmain">
        <div class="cc__rowt">Chat for {{ clientName }}</div>
        <div class="cc__rows">
          {{ settings.chatOn
            ? 'On — the panel is available to everyone whose group is switched on'
            : 'Off — the panel and the app’s chat item are gone for everyone in this client' }}
        </div>
      </div>
      <button type="button" class="cc__sw" role="switch" data-testid="cc-chat-on"
              :aria-checked="String(settings.chatOn)" :disabled="!mayManage"
              @click="toggleChat()"></button>
    </div>

    <div v-if="!settings.chatOn" class="cc__off" data-testid="cc-off-note">
      <strong>Chat is off for {{ clientName }}.</strong>
      Nothing below applies while it is off. Groups, office hours and the people named
      individually keep their settings and take effect again the moment chat is switched
      back on — nothing is lost by turning it off, and the conversation log stays in
      Reports for its 90 days.
    </div>

    <div v-else class="cc__cols">
      <!-- ── left: office hours, then who may post outside them ──────────── -->
      <div class="cc__col">
        <section class="cc__card">
          <h4>Office hours</h4>
          <p class="cc__lead">
            When the client is closed, chat is read-only: people can read and scroll, but not
            post. Nothing is hidden and nothing is deleted.
          </p>

          <div class="cc__master cc__master--sub">
            <div class="cc__rowmain">
              <div class="cc__rowt">Use a schedule</div>
              <div class="cc__rows">
                {{ hours.on ? openDaysSummary + ' · ' + hours.tz : 'Off — chat is open whenever chat itself is on' }}
              </div>
            </div>
            <button type="button" class="cc__sw" role="switch" data-testid="cc-hours-on"
                    :aria-checked="String(hours.on)" :disabled="!mayManage"
                    @click="hours.on = !hours.on; saveHours()"></button>
          </div>
          <!-- ⚠ #2099 — TRUE TODAY, AND IT SHOULD NOT BE. cs3-chat's
               OfficeHours::fromArray returns alwaysOpen() when `enabled` is
               false, discarding `days` and `tz` — so switching the schedule off
               throws the week away server-side however faithfully the screen
               sends it. Said out loud rather than letting a Client Admin find
               out by switching it back on. Flagged for a cs3-chat fix; when
               that lands, this note comes out. -->
          <p v-if="isLive && !hours.on" class="cc__hint" data-testid="cc-hours-not-kept">
            While the schedule is off, the chat service does not keep it: switching it back on
            starts from a blank week. Set the days again after you turn it on.
          </p>

          <template v-if="hours.on">
            <div class="cc__tz">
              <span class="cc__tzl">Timezone</span>
              <code>{{ hours.tz }}</code>
              <!-- ⚠ #2099 — THREE STATES, not two. The API returns the timezone
                   it stored and not where it came from, so `tzFromClient` is
                   null in a live build — and with a two-way ternary that
                   rendered the FALLBACK branch, captioning a real timezone
                   "this client has no timezone set". Unknown provenance says
                   nothing. -->
              <span v-if="hours.tzFromClient !== null" class="cc__tzs">{{
                hours.tzFromClient ? 'from the client record' : 'fallback — this client has no timezone set'
              }}</span>
            </div>

            <div class="cc__tw">
              <table class="cc__t">
                <thead><tr><th>Day</th><th>Open</th><th>From</th><th>To</th></tr></thead>
                <tbody>
                  <tr v-for="d in hours.days" :key="d.key" :class="{ 'cc__dim': !d.open }">
                    <td class="cc__nm">{{ d.label }}</td>
                    <td>
                      <button type="button" class="cc__sw cc__sw--sm" role="switch"
                              :aria-checked="String(d.open)" :aria-label="d.label"
                              :disabled="!mayManage" @click="d.open = !d.open; saveHours()"></button>
                    </td>
                    <!-- on `change`, not on every keystroke: a time input fires
                         input per digit, which would be four saves per edit. -->
                    <td v-if="d.open"><input type="time" class="cc__time" v-model="d.from" step="300"
                                             :disabled="!mayManage" @change="saveHours()"></td>
                    <td v-if="d.open"><input type="time" class="cc__time" v-model="d.to" step="300"
                                             :disabled="!mayManage" @change="saveHours()"></td>
                    <td v-if="!d.open" colspan="2" class="cc__closed">Closed</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- #2091 r5 — Ed, 17:33: "Schedule Only No Holidays". This is a
                 DECISION, not a gap waiting on one, so "yet" and "Flagged for
                 Ed" are both wrong: the schedule is weekly and that is the whole
                 of it. -->
            <p class="cc__hint">
              The schedule is weekly only — there is no holiday calendar. A statutory holiday
              is an ordinary open day, so close it on the day if the office is shut.
            </p>

            <h5 class="cc__sub">Allowed to post outside office hours</h5>
            <p class="cc__lead">
              A role switch covers everyone in that role. Name individual people on the right
              when the role switch is the wrong shape.
            </p>

            <selectBar :count="rolesAllowed" :total="roleKeys.length" noun="roles allowed"
                       @all="setAllRoles(true); saveRoles()" @none="setAllRoles(false); saveRoles()" />

            <div class="cc__tw">
              <table class="cc__t">
                <!-- #2091 r5 — Ed, 17:31, verbatim: "Allowed to post outside
                     office hours". The column said "Outside hours", which reads
                     as the hours themselves rather than as permission. -->
                <thead><tr><th>Role</th><th>Who it covers</th><th class="cc__r">Allowed to post outside office hours</th></tr></thead>
                <tbody>
                  <tr v-for="k in roleKeys" :key="k" :class="{ 'cc__dim': !hours.roles[k] }">
                    <td class="cc__nm">{{ rolePlural[k] }}</td>
                    <td class="cc__sublabel">{{ roleCovers[k] }}</td>
                    <td class="cc__r">
                      <button type="button" class="cc__sw" role="switch" :data-testid="'cc-role-' + k"
                              :aria-checked="String(hours.roles[k])"
                              :aria-label="rolePlural[k] + ' allowed to post outside office hours'"
                              :disabled="!mayManage"
                              @click="$set(hours.roles, k, !hours.roles[k]); saveRoles()"></button>
                    </td>
                  </tr>
                  <tr class="cc__dim">
                    <td class="cc__nm">CallSteward staff</td>
                    <td class="cc__sublabel">CS Admin and CS Support</td>
                    <td class="cc__r">
                      <button type="button" class="cc__sw" role="switch" aria-checked="true"
                              disabled aria-label="CallSteward staff, always allowed to post outside office hours"></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="cc__hint">CallSteward staff are never restricted by a client’s schedule.</p>

            <!-- ── #2106 — WHAT THE CLOSED PEOPLE READ ────────────────────────
                 Ed, 2026-10-21: "client chat box that speaks to users blocked by
                 hours should display a client defined message." It belongs in
                 this card and under these switches, because it is the sentence
                 the switches above cause someone to see.

                 Empty is a real state: the placeholder is the sentence
                 CallSteward shows in its place, so the Client Admin can read
                 what their people get before deciding to write anything. The
                 box is therefore never pre-filled with it — a pre-filled
                 default would be stored as the client's own words on the next
                 save of anything. -->
            <h5 class="cc__sub">What people see while the office is closed</h5>
            <p class="cc__lead">
              Shown in the chat box, in place of the message box, to anyone the switches above
              leave read-only. Your own words — plain text, up to {{ maxClosedMessage }}
              characters. Leave it empty to use CallSteward’s wording.
            </p>
            <!-- `@change`, not `@input`: a keystroke save is one request per
                 character, and the time inputs above settled this already. -->
            <textarea class="cc__msg" rows="3" data-testid="cc-closed-message"
                      v-model="hours.closedMessage" :maxlength="maxClosedMessage"
                      :placeholder="hours.closedDefault" :disabled="!mayManage"
                      aria-label="What people see while the office is closed"
                      @change="saveClosedMessage()"></textarea>
            <p class="cc__msgc">
              <span>{{ closedMessageLeft }} characters left</span>
              <span v-if="!closedMessageSet" class="cc__msgd" data-testid="cc-closed-default">
                Empty — your people see “{{ hours.closedDefault }}”
              </span>
            </p>
            <p class="cc__hint">
              CallSteward Support is never closed by a client’s hours, so this never covers it.
            </p>
          </template>
        </section>

        <!-- ── channel groups ─────────────────────────────────────────────── -->
        <section class="cc__card">
          <h4>Channel groups</h4>
          <p class="cc__lead">
            Which groups exist for this client. Membership comes from CS3 — roles, skills,
            statuses and job calls — so a group fills itself and stays current.
          </p>

          <selectBar :count="groupsOn" :total="groupKeys.length" noun="groups on"
                     @all="setAllGroups(true); saveGroups()" @none="setAllGroups(false); saveGroups()" />

          <div class="cc__tw">
            <table class="cc__t">
              <thead><tr><th>Group</th><th>Membership comes from</th><th class="cc__r">On</th></tr></thead>
              <tbody>
                <tr v-for="k in groupKeys" :key="k" :class="{ 'cc__dim': !settings.groups[k] }">
                  <td class="cc__nm">
                    {{ k }}
                    <span v-if="masterSwitches[k]" class="cc__master-tag">master</span>
                  </td>
                  <td class="cc__sublabel">
                    {{ groupSource[k] }}
                    <!-- #2091 r3 — Ed: "you need both to have a channel". Said
                         beside the switch, because a switch that only half
                         creates something has to say so where it is flicked. -->
                    <div v-if="masterSwitches[k]" class="cc__both">{{ masterSwitches[k] }}</div>
                  </td>
                  <td class="cc__r">
                    <button type="button" class="cc__sw" role="switch" :aria-label="k"
                            :aria-checked="String(settings.groups[k])"
                            :disabled="!mayManage"
                            @click="$set(settings.groups, k, !settings.groups[k]); saveGroups()"></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cc__hint">
            Switching a group off hides its channels from the panel. The conversation is kept
            and stays in Reports.
          </p>
          <p class="cc__hint">
            <strong>Job channels are time-boxed.</strong> A job's channel is open from
            <strong>24 hours before</strong> the job starts until <strong>24 hours after</strong> it
            ends. Outside that it stays readable but nobody can post.
          </p>

          <!-- ── #2121 — employers and the Client Admin ──────────────────────
               cs3-chat #37's per-client switch, `employer_ca_reach`. An employer
               with chat on already finds this client's Power Users and
               Stewards; with this on, the Client Admin is in that list too and
               can start a conversation with the employer. Off is what the chat
               did before #37.

               ⚠ DRAWN ONLY WHEN THE SERVER SENT THE FIELD. A chat server from
               before #37 has no such setting, and a switch reading "off"
               against it would describe a rule that does not exist and 422 on
               the first click. `employerCaReachKnown` is false until a read
               carries a boolean — including in the preview build, whose store
               has no such key. -->
          <div v-if="employerCaReachKnown" class="cc__master cc__master--sub cc__master--late"
               data-testid="cc-employer-ca-reach">
            <div class="cc__rowmain">
              <div class="cc__rowt">Employers can message the Client Admin</div>
              <div class="cc__rows">
                {{ settings.employerCaReach
                  ? 'On — an employer with chat on can find and message the Client Admin, and the Client Admin can message them'
                  : 'Off — employers reach Power Users and Stewards only' }}
              </div>
            </div>
            <button type="button" class="cc__sw" role="switch" data-testid="cc-employer-ca-reach-on"
                    aria-label="Employers can message the Client Admin"
                    :aria-checked="String(settings.employerCaReach)" :disabled="!mayManage"
                    @click="toggleEmployerCaReach()"></button>
          </div>
          <p v-if="employerCaReachKnown" class="cc__hint">
            Switching it off makes an open employer–Client Admin conversation read-only from the
            next message; nothing is deleted, and switching it back on reopens it.
          </p>

          <!-- #2091 r3 — "you need both to have a channel" is a rule whose effect
               is an ABSENCE, and an absent channel is invisible. This table makes
               it visible: the master switch above, each item's own Chat option,
               and the channel that does or does not result.
               ⚠ #2099 — PREVIEW ONLY. Its rows are the invented jobs and
               employers in chatPreview.js, so in a live build it would show a
               real Client Admin channels that do not exist. The rule it
               illustrates is unchanged and still stated in the two notes above. -->
          <template v-if="!isLive">
          <h5 class="cc__sub">Where job and employer channels come from</h5>
          <p class="cc__lead">
            The switches above <em>permit</em> these channels. Each job and each employer also has
            its own Chat option, set on the job or the employer. A channel exists only where both
            are on.
          </p>
          <div class="cc__tw">
            <table class="cc__t">
              <thead><tr><th>Job or employer</th><th>Client switch</th><th>Its own Chat option</th><th>Channel?</th></tr></thead>
              <tbody>
                <tr v-for="row in bothSwitchRows" :key="row.id" :class="{ 'cc__dim': !row.channel }">
                  <td class="cc__nm">{{ row.label }}</td>
                  <td class="cc__sublabel">{{ row.master ? 'on' : 'off' }}</td>
                  <td class="cc__sublabel">{{ row.own ? 'on' : 'off' }}</td>
                  <td :class="row.channel ? 'cc__yes' : 'cc__no'">
                    {{ row.channel ? 'yes' : 'no channel' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cc__hint">
            Turn the <strong>Job</strong> or <strong>Employers / Payroll</strong> switch above off and
            every row under it loses its channel, whatever each item's own option says.
          </p>
          </template>
        </section>
      </div>

      <!-- ── right: people named individually, then global access ────────── -->
      <div class="cc__col">
        <!-- ⚠ #2099 — PREVIEW ONLY, AND THIS ONE IS A GAP, NOT A CHOICE.
             The chat service DOES store per-person outside-hours permission,
             as `outside_hours.people = { chat uid: bool }`. But this card
             names people by `employee_id` (spec row 21 asks for exactly that)
             and shows a name, role, phone and email from a directory search —
             and employee_id is not the CS3 user id the chat service keys on,
             with nowhere to put a name and no employee search behind it on
             either side. So the list cannot round-trip yet.
             Showing it live would be a card whose every edit is silently
             discarded, which is the one thing the removed preview banner used
             to excuse. Listed as a gap on #2099 instead of given invented
             storage. The ROLE switches, which the API does store, are wired. -->
        <section v-if="!isLive" class="cc__card">
          <h4>People named individually</h4>
          <p class="cc__lead">
            A person named here overrides their role, either way. Precedence is person, then
            role, then the schedule.
          </p>

          <input type="search" class="cc__search" v-model="search" data-testid="cc-search"
                 placeholder="Search employees — name, phone or email"
                 aria-label="Search employees to name individually">

          <template v-if="search.trim()">
            <p v-if="!hits.length" class="cc__hint">
              No employee matches “{{ search.trim() }}” who is not already named below.
            </p>
            <template v-else>
              <selectBar :count="pickedCount" :total="hits.length" noun="found"
                         @all="pickAll(true)" @none="pickAll(false)" />
              <div class="cc__tw">
                <table class="cc__t">
                  <thead><tr><th class="cc__tick"></th><th>Name</th><th>employee_id</th><th>Role</th><th>Phone</th><th>Email</th></tr></thead>
                  <tbody>
                    <tr v-for="d in hits" :key="d.eid" class="cc__pick" :class="{ 'cc__on': !!picked[d.eid] }"
                        role="button" tabindex="0" :aria-pressed="String(!!picked[d.eid])"
                        @click="togglePick(d)" @keydown.space.prevent="togglePick(d)" @keydown.enter.prevent="togglePick(d)">
                      <td class="cc__tick"><span class="cc__box" aria-hidden="true"></span></td>
                      <td class="cc__nm">{{ d.name }}</td>
                      <td class="cc__mono">{{ d.eid }}</td>
                      <td class="cc__sublabel">{{ roleLabel[d.role] }}</td>
                      <td class="cc__mono">{{ d.phone }}</td>
                      <td class="cc__mono">{{ d.email }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button v-if="pickedCount" type="button" class="c-btn cc__btn" data-testid="cc-add"
                      @click="addPicked()">
                Add {{ pickedCount }} {{ pickedCount === 1 ? 'person' : 'people' }} to the list
              </button>
            </template>
          </template>

          <h5 class="cc__sub">Named for this client</h5>
          <p v-if="!hours.people.length" class="cc__hint">
            Nobody is named individually. Everyone follows their role’s switch.
          </p>
          <template v-else>
            <selectBar :count="peopleAllowed" :total="hours.people.length" noun="allowed"
                       @all="setAllPeople(true)" @none="setAllPeople(false)" />
            <div class="cc__tw">
              <table class="cc__t">
                <thead><tr><th>Name</th><th>employee_id</th><th>Role</th><th>Allowed to post outside office hours</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="(x, i) in hours.people" :key="x.eid" :class="{ 'cc__dim': !x.allowed }">
                    <td class="cc__nm">{{ x.name }}</td>
                    <td class="cc__mono">{{ x.eid }}</td>
                    <td class="cc__sublabel">{{ roleLabel[x.role] }}</td>
                    <td>
                      <div class="cc__swwrap">
                        <button type="button" class="cc__sw" role="switch"
                                :aria-label="x.name + ' allowed to post outside office hours'"
                                :aria-checked="String(x.allowed)" @click="x.allowed = !x.allowed"></button>
                        <span class="cc__swl">{{ personLabel(x) }}</span>
                      </div>
                    </td>
                    <td class="cc__r">
                      <button type="button" class="cc__rm" @click="hours.people.splice(i, 1)">Remove</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="cc__hint">
              Removing someone does not block them — it returns them to whatever their role allows.
            </p>
          </template>
        </section>

        <!-- ⚠ #2099 — PREVIEW ONLY. A global-channel grant is a column on the
             chat service's `chat_users` (`global_granted`), not part of the
             client settings, and the service exposes no endpoint for it today
             (`routes/api.php` has no grant route). Nothing here can be saved,
             so it is not shown live. Listed as a gap on #2099. -->
        <section v-if="!isLive" class="cc__card">
          <h4>Access to CS-world channels</h4>
          <p class="cc__lead">
            Only Client Admins, Power Users and Stewards can be let into the global channels,
            and only you can let them in.
          </p>

          <selectBar :count="grantsOn" :total="grantable.length" noun="granted"
                     @all="setAllGrants(true)" @none="setAllGrants(false)" />

          <div class="cc__tw">
            <table class="cc__t">
              <thead><tr><th>Name</th><th>Role</th><th>Global access</th></tr></thead>
              <tbody>
                <tr v-for="g in settings.grants" :key="g.name" :class="{ 'cc__dim': !g.granted }">
                  <td class="cc__nm">{{ g.name }}</td>
                  <td class="cc__sublabel">{{ roleLabel[g.role] }}</td>
                  <td>
                    <div class="cc__swwrap">
                      <button type="button" class="cc__sw" role="switch" :aria-label="g.name"
                              :aria-checked="String(g.granted)" :disabled="g.role === 'member'"
                              @click="g.granted = !g.granted"></button>
                      <span class="cc__swl">{{ grantLabel(g) }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cc__hint">
            A grant cancels itself: if someone’s role or client changes in CS3, their global
            access is revoked automatically and you have to grant it again.
          </p>
        </section>
      </div>
    </div>

    <section class="cc__card cc__card--wide">
      <h4>Reading the conversation log</h4>
      <p class="cc__lead">
        You can read your members’ chats within the 90 days chat keeps — in Reports, not in the
        chat panel. The panel only ever shows channels you are in, for everyone including you.
      </p>
      <p class="cc__hint">
        <strong>Reports · Chat log.</strong> By channel, date range and sender, scoped to
        <code>{{ clientName }}</code>. A Power User can pull the same report. Older than 90 days
        is recovered from the SQL backup, not from a report.
      </p>
    </section>

    </template>
  </div>
</template>

<script>
/**
 * #2091 — Configuration -> Chat, the real screen behind the CHAT_UI_PREVIEW flag.
 *
 * Content follows the approved wireframe (cs3-chat !4 round 4). Rendered with
 * the Configuration area's own classes — `col-md-10 col-sm-9 u-pad-all-25
 * c-common-box-shadow u-bg-color-fff`, `h3.u-cmn-botmargin`,
 * `p.o-blue-regular-text` — so it is the same screen as its neighbours rather
 * than a lookalike; only what the wireframe adds is styled here.
 *
 * ⚠ NOTHING HERE PERSISTS, AND NOTHING IS FETCHED. There is no chat back end
 * (cs3-chat #6-#10, #18). This component imports no service and makes no
 * request: the data comes from `chatPreview.js` and edits live in local state
 * until the page reloads. The banner at the top says so, because a settings
 * screen that silently discards changes is worse than one that admits it.
 */
import selectBar from './chatSelectBar.vue';
import {
    CHANNELS, DIRECTORY, FAKE_CLIENT, GROUP_SOURCE, MASTER_SWITCHES,
    ROLE_COVERS, ROLE_LABEL, ROLE_PLURAL,
    chatLiveEnabled, previewStore,
} from '../../chatPreview.js';
// #2099 — the real settings, through the same chat session the panel uses.
import {
    ChatUnavailable, fetchClientSettings, saveClientSettings, signIn,
} from '../../chatService.js';
import {
    MAX_CLOSED_MESSAGE, employerCaReachPatch, enabledPatch, kindsPatch, mayManage, officeHoursPatch,
    outsideHoursPatch, toScreen,
} from '../../chatSettings.js';

/**
 * #2099 — the shape the screen needs before the server has answered.
 *
 * Every switch OFF and no groups, which is why the template refuses to render
 * the body until `liveState === 'ready'`: a Client Admin reading "chat is off"
 * while the request is in flight, or flicking a switch against a state the
 * screen has not loaded, is the opposite of what this screen is for. This
 * exists so the component can mount at all, not so it can be shown.
 */
function blankSettings() {
    // #2121 — `employerCaReach` is listed here so Vue observes it: the loaded
    // value arrives by Object.assign onto this object, and a key absent from
    // the initial data would flip on click without the switch moving.
    return { chatOn: false, groups: {}, grants: [], hours: toScreen({}).hours, employerCaReach: null };
}

export default {
    name: 'configChat',
    components: { selectBar },
    props: ['empDetails'],
    data() {
        return {
            /*
             * #2099 — in a live build this is replaced by the client's own name
             * from the handoff. The preview's invented local must never head a
             * screen that is about to write real settings.
             */
            clientName: chatLiveEnabled() ? 'this client' : FAKE_CLIENT,
            /*
             * The SHARED preview store, not a private copy: switching chat off
             * here makes the panel disappear, and closing today's hours makes
             * its composer read-only. That is how the wireframe's states are
             * reached in the real app, without mock-up controls.
             *
             * ⚠ #2099 — IN A LIVE BUILD THIS IS A PRIVATE OBJECT, loaded from
             * the chat service in `mounted`. Sharing it with the panel would be
             * wrong twice over: the panel's live state comes from the server
             * too, and an unsaved toggle here would silently change what the
             * panel shows before anyone pressed anything.
             */
            settings: chatLiveEnabled() ? blankSettings() : previewStore(),

            // ── #2099 live state ───────────────────────────────────────────
            // 'idle' | 'loading' | 'ready' | 'failed'
            liveState: 'idle',
            liveError: null,
            saveError: null,
            // #2099 — the chat service's per-field 422 messages, shown verbatim.
            // The screen has no per-control slots, so the field travels with
            // the message.
            saveFieldErrors: [],
            saving: false,
            savedAt: null,
            // The chat role the server gave us, which decides whether this
            // screen may save at all.
            chatRole: null,
            /*
             * `outside_hours.people` exactly as the API last returned it. The
             * API replaces a section whole, so a roles-only save that omitted
             * this would DELETE every individually-named person — and this
             * screen cannot render that list yet (see chatSettings.toScreen).
             */
            knownPeople: {},
            search: '',
            picked: {},
            // #2106 — cs3-chat's own cap (OutsideHoursRules::MAX_MESSAGE), on
            // the box as `maxlength`, so its 422 cannot be reached by typing.
            maxClosedMessage: MAX_CLOSED_MESSAGE,
            // #2091 r5 — Employers beside the other four (Ed, 17:32).
            roleKeys: ['ca', 'pu', 'steward', 'member', 'employer'],
            roleLabel: ROLE_LABEL,
            rolePlural: ROLE_PLURAL,
            roleCovers: ROLE_COVERS,
            groupSource: GROUP_SOURCE,
            masterSwitches: MASTER_SWITCHES,
        };
    },
    computed: {
        /** #2099 — is this screen talking to the real chat service? */
        isLive() { return chatLiveEnabled(); },

        /** May this user save? The service decides; this only avoids offering it. */
        mayManage() { return !this.isLive || mayManage(this.chatRole); },

        /**
         * #2099 — a failure code turned into something a Client Admin can act
         * on. Each of these is a real state the chat service can put the screen
         * in, and they want different responses: wait, reload, or tell the
         * office.
         */
        liveErrorText() {
            return {
                chat_not_permitted: 'Your role cannot change this client’s chat settings.',
                chat_session_expired: 'Your chat sign-in expired. Reload the page.',
                chat_no_client: 'This screen needs a local selected. Pick one and reload.',
                chat_settings_rejected: 'The chat service refused that setting.',
                chat_client_not_addressable: 'Chat cannot address this local yet. Tell the office.',
                chat_unreachable: 'Chat is unreachable right now.',
                no_logged_in_client: 'Chat needs a local selected. Pick one and reload.',
                handoff_rejected: 'Your chat sign-in expired. Reload the page.',
                cs3_handoff_incomplete: 'Chat is not configured on this server.',
                chat_session_missing: 'Chat is not configured on this server.',
            }[this.saveError || this.liveError] || 'Chat is unavailable.';
        },

        hours() { return this.settings.hours; },
        /**
         * #2121 — did the chat server say whether employers may message the
         * Client Admin? Only a boolean counts: null is "an older server, no
         * such setting" and the preview store has no key at all — the switch
         * is drawn for neither.
         */
        employerCaReachKnown() { return typeof this.settings.employerCaReach === 'boolean'; },
        groupKeys() { return Object.keys(this.settings.groups); },
        groupsOn() { return this.groupKeys.filter((k) => this.settings.groups[k]).length; },
        rolesAllowed() { return this.roleKeys.filter((k) => this.hours.roles[k]).length; },
        /** #2106 — has the Client Admin written wording of their own? */
        closedMessageSet() { return (this.hours.closedMessage || '').trim() !== ''; },
        closedMessageLeft() {
            return this.maxClosedMessage - (this.hours.closedMessage || '').length;
        },
        peopleAllowed() { return this.hours.people.filter((x) => x.allowed).length; },
        /** Members can never be granted, so they are not part of the count. */
        grantable() { return this.settings.grants.filter((g) => g.role !== 'member'); },
        grantsOn() { return this.grantable.filter((g) => g.granted).length; },
        pickedCount() { return this.hits.filter((d) => this.picked[d.eid]).length; },
        hits() {
            const q = this.search.trim().toLowerCase();
            if (!q) return [];
            const already = {};
            this.hours.people.forEach((x) => { already[x.eid] = true; });
            const digits = q.replace(/[^0-9]/g, '');

            return DIRECTORY.filter((d) => {
                if (already[d.eid]) return false;
                if (d.name.toLowerCase().indexOf(q) >= 0) return true;
                if (d.email.toLowerCase().indexOf(q) >= 0) return true;

                return digits.length >= 3 && d.phone.replace(/[^0-9]/g, '').indexOf(digits) >= 0;
            });
        },
        /**
         * #2091 r3 — one row per job/employer channel in the fake data, showing
         * the client master switch, the item's own Chat option, and whether a
         * channel results. Reads the live switches above, so flicking one
         * changes the answers.
         */
        bothSwitchRows() {
            return CHANNELS
                .filter((c) => c.job || c.employer)
                .map((c) => {
                    const group = c.job ? 'Job' : 'Employers / Payroll';
                    const master = this.settings.groups[group] !== false;
                    const own = c.job ? c.job.chatOn : c.employer.chatOn;

                    return {
                        id: c.id,
                        label: c.label.replace(/^(Job · |Employers \/ Payroll · )/, ''),
                        master,
                        own,
                        channel: master && own,
                    };
                });
        },
        openDaysSummary() {
            const open = this.hours.days.filter((d) => d.open).length;

            return open ? open + ' of 7 days open' : 'Closed every day';
        },
    },
    methods: {
        // ── #2099: the live settings ───────────────────────────────────────

        /**
         * Sign in to chat and load this client's real settings.
         *
         * ⚠ NOTHING HERE THROWS PAST THIS METHOD. Configuration is the rest of
         * CS3's settings area; a chat service that is down must leave the other
         * items on it usable. Every failure becomes `liveState = 'failed'` plus
         * a line the Client Admin can read.
         */
        async loadLive() {
            this.liveState = 'loading';
            this.liveError = null;

            try {
                const session = await signIn();
                this.chatRole = session.role;
                /*
                 * ⚠ #2117 — THE CLIENT WHOSE SETTINGS ARE ON SCREEN, which for
                 * CS staff is not the client their session belongs to: staff
                 * have none (`cid` null, nothing billed) and administer the one
                 * they are working in. Every heading on this screen names the
                 * client whose switches these are — "Chat is off for X", "live
                 * for everyone in X" — so taking the wrong one would have staff
                 * editing one local's settings under another's name.
                 *
                 * `clientName` is still the fallback for a client user, where
                 * the two are the same client.
                 */
                const named = session.settingsClientName || session.clientName;
                if (named) this.clientName = named;

                this.applyServer(await fetchClientSettings(session));
                this.liveState = 'ready';
            } catch (e) {
                this.liveState = 'failed';
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                console.warn('[chat settings] could not load', e);
            }
        },

        /** The API's settings → the screen, and remember what we must carry through. */
        applyServer(api) {
            this.settings = Object.assign(this.settings, toScreen(api));
            /*
             * Kept verbatim: `outside_hours` is replaced WHOLE on save, so a
             * roles-only save that omitted `people` would delete every
             * individually-named person. The screen cannot render that list
             * yet, so it must not be the thing that destroys it.
             */
            const outside = (api && api.outside_hours) || {};
            this.knownPeople = (outside.people && typeof outside.people === 'object') ? outside.people : {};
        },

        /**
         * Save one section.
         *
         * ⚠ SERIALISED, AND THE LAST WRITE OF A SECTION WINS. Every switch on
         * this screen saves as it is flicked — there is no Save button, and
         * adding one would be a different screen — so three quick toggles are
         * three requests. Without a queue they race and the slowest answer is
         * the one that sticks, which on a settings screen means a switch that
         * springs back minutes later.
         *
         * `_saveChain` is a promise chain, not a debounce: each save still
         * happens, in order, so the server ends in the state the screen shows.
         */
        saveSection(patch) {
            if (!this.isLive || this.liveState !== 'ready') return Promise.resolve();

            this.saving = true;
            this.saveError = null;

            this._saveChain = (this._saveChain || Promise.resolve())
                .catch(() => {})     // a previous failure must not stop this one
                .then(async () => {
                    try {
                        const fresh = await saveClientSettings(await signIn(), patch);
                        /*
                         * ⚠ CLEARED HERE, NOT WHERE THE SAVE WAS QUEUED.
                         *
                         * Clearing at queue time meant: save A fails, save B
                         * succeeds, and nothing clears A's error — so the
                         * banner reads "your change has not been saved" over a
                         * server that holds the right value. On a settings
                         * screen that is the worst direction to be wrong in.
                         */
                        this.saveError = null;
                        this.saveFieldErrors = [];
                        /*
                         * Trust the SERVER's answer over the screen's optimism.
                         * The API returns the whole settings object after a
                         * write, so anything it normalised, refused or capped
                         * is reflected rather than left looking saved.
                         */
                        if (fresh) this.applyServer(fresh);
                        this.savedAt = Date.now();
                    } catch (e) {
                        this.saveError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                        // The API's own per-field messages, when it sent any.
                        this.saveFieldErrors = (e && e.fieldErrors) || [];
                        console.warn('[chat settings] save failed', e);
                        /*
                         * Re-read, so the screen shows what the server actually
                         * holds rather than the change that did not land. A
                         * switch left looking "on" after a failed save is the
                         * worst outcome here — the CA would believe chat is on.
                         */
                        try {
                            this.applyServer(await fetchClientSettings(await signIn()));
                        } catch (re) { /* already reporting the first failure */ }
                    }
                })
                .then(() => { this.saving = false; });

            return this._saveChain;
        },

        /** The master switch. */
        toggleChat() {
            if (!this.mayManage) return;
            this.settings.chatOn = !this.settings.chatOn;
            this.saveSection(enabledPatch(this.settings.chatOn));
        },

        /** A group switch, or Select all / none. */
        saveGroups() { this.saveSection(kindsPatch(this.settings.groups)); },

        /**
         * #2121 — "Employers can message the Client Admin". Its own one-key
         * save: the API applies every key it is sent, so riding along with a
         * group save would write this screen's copy over another tab's change.
         */
        toggleEmployerCaReach() {
            if (!this.mayManage || !this.employerCaReachKnown) return;
            this.settings.employerCaReach = !this.settings.employerCaReach;
            this.saveSection(employerCaReachPatch(this.settings.employerCaReach));
        },

        /** The office-hours card: the on/off, the day rows, the timezone. */
        saveHours() { this.saveSection(officeHoursPatch(this.hours)); },

        /**
         * The "Allowed to post outside office hours" role switches.
         *
         * ⚠ #2106 — AND NO MESSAGE. `outside_hours` is replaced whole except
         * for `message`, which cs3-chat KEEPS when the section arrives without
         * it. Sending this screen's copy from a role toggle would overwrite the
         * CA's wording with whatever was last loaded — including from another
         * tab, or from before someone else edited it.
         */
        saveRoles() { this.saveSection(outsideHoursPatch(this.hours.roles, this.knownPeople)); },

        /**
         * #2106 — the closed message, and only it.
         *
         * Sent as its own save (with the roles and people carried through,
         * because the section still replaces those whole). An empty box sends
         * `''`, which is how cs3-chat CLEARS the wording and brings its own
         * sentence back — absent would have meant "keep", which is the opposite.
         */
        saveClosedMessage() {
            if (!this.mayManage) return;
            this.saveSection(outsideHoursPatch(
                this.hours.roles, this.knownPeople, (this.hours.closedMessage || '').trim(),
            ));
        },

        setAllRoles(on) { this.roleKeys.forEach((k) => this.$set(this.hours.roles, k, on)); },
        setAllGroups(on) { this.groupKeys.forEach((k) => this.$set(this.settings.groups, k, on)); },
        setAllPeople(on) { this.hours.people.forEach((x) => { x.allowed = on; }); },
        setAllGrants(on) { this.grantable.forEach((g) => { g.granted = on; }); },
        togglePick(d) {
            if (this.picked[d.eid]) this.$delete(this.picked, d.eid);
            else this.$set(this.picked, d.eid, true);
        },
        pickAll(on) {
            this.hits.forEach((d) => {
                if (on) this.$set(this.picked, d.eid, true);
                else this.$delete(this.picked, d.eid);
            });
        },
        addPicked() {
            this.hits.forEach((d) => {
                if (!this.picked[d.eid]) return;
                // Added NOT allowed: naming someone starts a decision, it does
                // not make it. The Client Admin then says which way.
                this.hours.people.push({ eid: d.eid, name: d.name, role: d.role, allowed: false });
            });
            this.picked = {};
            this.search = '';
        },
        personLabel(x) {
            if (x.allowed) return 'Allowed';

            return this.hours.roles[x.role] ? 'Read-only — overrides their role' : 'Read-only';
        },
        grantLabel(g) {
            if (g.role === 'member') return 'Members can never be granted';

            return g.granted ? 'Granted by you' : 'Not granted';
        },
    },
    mounted() {
        /*
         * #2099 — load the real settings. Not awaited: Configuration must
         * render whether or not the chat service answers, and `liveState`
         * carries the outcome to the screen.
         */
        if (this.isLive) this.loadLive();
    },
};
</script>

<style scoped>
.cc__preview{
    display:flex;align-items:flex-start;gap:9px;
    margin:0 0 20px;padding:11px 13px;
    background:#fff6e0;border:1px solid #fb8b03;border-radius:4px;
    font-size:13px;line-height:1.5;color:#6b4a00;
}
.cc__preview i{margin-top:2px;color:#fb8b03}

.cc__master{
    display:flex;align-items:center;gap:12px;
    padding:13px 15px;margin:0 0 18px;
    background:#eef4ff;border:1px solid #c9d9f5;border-radius:4px;
}
.cc__master--sub{margin-bottom:13px}
/* #2121 — the employer ↔ Client Admin row sits under the group table's hints. */
.cc__master--late{margin-top:18px;margin-bottom:0}
.cc__rowmain{flex:1;min-width:0}
.cc__rowt{font-weight:600;font-size:14px;color:#222}
.cc__rows{font-size:12px;color:#6b7280;margin-top:2px}
.cc__off{
    padding:13px 15px;background:#fff6e0;border:1px solid #fb8b03;border-radius:4px;
    font-size:13px;line-height:1.55;color:#6b4a00;max-width:80ch;
}

.cc__cols{display:flex;gap:26px;align-items:flex-start;flex-wrap:wrap}
.cc__col{flex:1 1 420px;min-width:0;display:flex;flex-direction:column;gap:22px}
/* #2099 — one line per refused field, inside the save-error banner. */
.cc__fielderr{display:block;margin-top:3px;font-size:12px}
.cc__fielderr code{
    padding:1px 4px;border-radius:3px;background:rgba(0,0,0,.06);
    font-size:11px;color:inherit;
}
.cc__saved{background:#eef7ee;border-color:#cfe6cf;color:#2f6b2f}
.cc__card{min-width:0}
.cc__card--wide{margin-top:22px;padding-top:20px;border-top:1px solid #dcdfe6}
.cc__card h4{
    margin:0 0 4px;font-size:13px;font-weight:700;text-transform:uppercase;
    letter-spacing:.07em;color:#6b7280;
}
.cc__sub{
    margin:20px 0 4px;padding-top:15px;border-top:1px solid #e8ebf0;
    font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.085em;color:#6b7280;
}
.cc__lead{margin:0 0 13px;font-size:12.5px;line-height:1.5;color:#6b7280;max-width:62ch}
.cc__hint{
    margin:11px 0 0;padding:8px 10px;background:#f4f7f8;border:1px solid #dcdfe6;
    border-radius:4px;font-size:11.5px;line-height:1.45;color:#6b7280;
}
.cc__hint code{font-size:11px;color:#2451a8;background:none;padding:0}

.cc__tz{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding:9px 0 3px;font-size:11.5px;color:#6b7280}
.cc__tzl{font-weight:600;color:#4a5562}
.cc__tz code{font-size:11px;color:#2451a8;background:none;padding:0}
.cc__tzs{color:#8b949e}

.cc__tw{overflow-x:auto}
.cc__t{width:100%;border-collapse:collapse;font-size:12.5px}
.cc__t th{
    text-align:left;padding:7px 10px 7px 0;border-bottom:1px solid #dcdfe6;
    font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;
    font-weight:600;white-space:nowrap;
}
.cc__t td{padding:8px 10px 8px 0;border-bottom:1px solid #e8ebf0;color:#4a5562;vertical-align:middle}
.cc__t tr:last-child td{border-bottom:0}
.cc__t th:last-child,.cc__t td:last-child{padding-right:0}
.cc__nm{color:#222;font-weight:600;white-space:nowrap}
.cc__sublabel{color:#6b7280}
.cc__both{margin-top:3px;font-size:11px;line-height:1.45;color:#9a6a00}
.cc__yes{color:#17a068;font-weight:600}
.cc__no{color:#8b949e}
.cc__master-tag{
    margin-left:6px;padding:1px 5px;border-radius:3px;
    background:#fff6e0;border:1px solid #fb8b03;color:#9a6a00;
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9px;
    font-weight:400;letter-spacing:.03em;text-transform:uppercase;
}
.cc__mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;color:#6b7280;white-space:nowrap}
.cc__r{text-align:right;white-space:nowrap}
.cc__dim .cc__nm{color:#6b7280}
.cc__closed{color:#8b949e;font-style:italic}
.cc__swwrap{display:flex;align-items:center;gap:9px}
.cc__swl{font-size:11.5px;color:#6b7280}

.cc__pick{cursor:pointer}
.cc__pick:hover td{background:#f6f8fb}
.cc__pick.cc__on td{background:#eef4ff}
.cc__tick{width:26px;padding-right:0}
.cc__box{
    display:block;width:15px;height:15px;border-radius:3px;position:relative;
    border:1px solid #dcdfe6;background:#fff;
}
.cc__pick.cc__on .cc__box{background:#2451a8;border-color:#2451a8}
.cc__pick.cc__on .cc__box::after{
    content:"";position:absolute;left:4px;top:1px;width:4px;height:8px;
    border:solid #fff;border-width:0 2px 2px 0;transform:rotate(42deg);
}

.cc__search{
    width:100%;padding:7px 9px;font-size:12.5px;color:#222;
    background:#fff;border:1px solid #dcdfe6;border-radius:4px;margin-bottom:4px;
}
.cc__search:focus{border-color:#2451a8;outline:none}
.cc__btn{margin-top:12px;background:#2451a8;color:#fff;border:1px solid #2451a8;border-radius:4px;padding:7px 14px;font-size:12.5px;font-weight:600}
.cc__btn:hover{background:#1b3f85;border-color:#1b3f85}
.cc__rm{background:none;border:1px solid #dcdfe6;border-radius:3px;padding:3px 8px;font-size:11px;color:#6b7280}
.cc__rm:hover{border-color:#a8252e;color:#a8252e}

.cc__time{
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;color:#222;
    padding:4px 5px;border:1px solid #dcdfe6;border-radius:3px;background:#fff;
}
.cc__time:focus{border-color:#2451a8;outline:none}

/* #2106 — the closed message. As wide as the card's prose, so what the CA
   types is laid out roughly as their people will read it in the panel. */
.cc__msg{
    display:block;width:100%;max-width:62ch;box-sizing:border-box;resize:vertical;
    font-family:inherit;font-size:12.5px;line-height:1.5;color:#222;
    padding:7px 9px;border:1px solid #dcdfe6;border-radius:4px;background:#fff;
}
.cc__msg:focus{border-color:#2451a8;outline:none}
.cc__msg:disabled{background:#f4f7f8;color:#6b7280}
/* A counter, not a hint: the boxed `cc__hint` treatment reads as advice and
   crowds the card between the box and the Support note below it. */
.cc__msgc{
    display:flex;gap:10px;justify-content:space-between;flex-wrap:wrap;max-width:62ch;
    margin:5px 0 0;font-size:11.5px;line-height:1.45;color:#8f98a6;
}
.cc__msgd{color:#8a6d1f}

.cc__sw{
    flex:0 0 38px;width:38px;height:21px;border-radius:11px;position:relative;
    background:#c4ccd8;border:0;padding:0;transition:background .13s;cursor:pointer;
}
.cc__sw::after{
    content:"";position:absolute;top:2px;left:2px;width:17px;height:17px;border-radius:50%;
    background:#fff;transition:transform .13s;box-shadow:0 1px 2px rgba(0,0,0,.26);
}
.cc__sw[aria-checked="true"]{background:#2451a8}
.cc__sw[aria-checked="true"]::after{transform:translateX(17px)}
.cc__sw:disabled{opacity:.42;cursor:not-allowed}
.cc__sw--sm{flex:0 0 32px;width:32px;height:18px}
.cc__sw--sm::after{width:14px;height:14px}
.cc__sw--sm[aria-checked="true"]::after{transform:translateX(14px)}
</style>
